/**
 * React Application Entry Point
 */

import "helpers/initFA";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { PermissionProvider } from "react-permission-role"; // For role-based permissions
import { Provider, useDispatch, useSelector } from "react-redux";
import { RouterProvider } from "react-router-dom"; // Use only RouterProvider

import { logout } from "features/auth/authSlice";
import { selectCurrentLanguage } from "features/language/languageSlice";
import i18n from "i18n";
import AppProvider from "providers/AppProvider";
import { router } from "routes"; // Define routes using createBrowserRouter
import { store } from "store/store";

import { isTokenExpired } from "components/utilities/sedra3/auth";

import paths from "routes/paths";

/**
 * AppWithPermissions Component
 * Handles token validation, permissions, and routing.
 */
const AppWithPermissions = () => {
  const dispatch = useDispatch();

  // Extract roles, permissions, and token from Redux state
  const { roles, permissions, token } = useSelector((state) => state.auth);

  // Get the current language from Redux
  const currentLanguage = useSelector(selectCurrentLanguage);

  // Define public paths that don't require a token
  const publicPaths = [
    paths.cardLogin,
    paths.cardConfirmMail,
    paths.cardForgotPassword,
    paths.cardResetPassword,
    paths.simpleLogin,
    paths.simpleForgotPassword,
    paths.simpleResetPassword,
    paths.cardTechnicalIssue,
  ];

  // Define public path patterns (for dynamic routes like confirm-email with token)
  const publicPathPatterns = [/^\/authentication\/card\/confirm-email\/[^/]+$/];

  /**
   * Synchronize the initial language from localStorage on component mount.
   */
  useEffect(() => {
    const storedLanguage = localStorage.getItem("language") || "en";
    i18n.changeLanguage(storedLanguage); // Set the language in i18n
  }, []);

  /**
   * Synchronize i18n with Redux language updates.
   */
  useEffect(() => {
    if (currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage]);

  /**
   * Check token expiry and redirect to the login page if the token is invalid.
   */
  useEffect(() => {
    const currentPath = window.location.pathname;

    // Check if current path is in publicPaths or matches any publicPathPatterns
    const isPublicPath =
      publicPaths.includes(currentPath) ||
      publicPathPatterns.some((pattern) => pattern.test(currentPath));

    if ((!token || isTokenExpired(token)) && !isPublicPath) {
      dispatch(logout()); // Dispatch logout action
      router.navigate(paths.cardLogin); // Redirect to login
    }
  }, [token, dispatch]);

  return (
    <PermissionProvider roles={roles} permissions={permissions}>
      <RouterProvider router={router} />
    </PermissionProvider>
  );
};

/**
 * Mount the application to the DOM
 */
const container = document.getElementById("main");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <AppProvider>
        <I18nextProvider i18n={i18n}>
          <AppWithPermissions />
        </I18nextProvider>
      </AppProvider>
    </Provider>
  </React.StrictMode>
);
